import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Download, ChevronRight, Truck, Store, Loader2 } from "lucide-react";
import api, { API, TOKEN_KEY, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const FILTERS = [
  { v: "all", t: "Tout", testId: "subscribers-filter-all" },
  { v: "decouverte", t: "Découverte" },
  { v: "prestige", t: "Prestige" },
  { v: "livraison", t: "Livraison" },
  { v: "retrait", t: "Retrait" },
  { v: "en_attente", t: "Mensualité due" },
  { v: "paye", t: "Mensualité OK" },
  { v: "pay_pending", t: "À payer (carte)" },
  { v: "pay_paid", t: "Payés (carte)" },
  { v: "a_preparer", t: "À préparer" },
  { v: "termine", t: "Terminé" },
];

function AddClientDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", plan: "decouverte",
    deliveryMethod: "retrait", address: "", postalCode: "", city: "", firstDate: "", comment: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/subscribers", { ...form, isAdult: true, acceptTerms: true });
      toast.success("Abonné ajouté");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none overflow-y-auto no-scrollbar bg-[#FDFBF7] p-5 pb-56 sm:p-6 sm:pb-6 sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:top-[50%] sm:left-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-[#581825]">Ajouter un abonné</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} /></div>
            <div><Label>Nom</Label><Input required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>Téléphone</Label><Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Formule</Label>
              <RadioGroup value={form.plan} onValueChange={(v) => set("plan", v)} className="flex gap-2 mt-1">
                {[["decouverte", "Découverte"], ["prestige", "Prestige"]].map(([v, t]) => (
                  <label key={v} className={`flex-1 text-center text-sm rounded-lg border p-2 cursor-pointer ${form.plan === v ? "border-[#581825] bg-[#F5EFEB]" : "border-[#E5DEC9]"}`}>
                    <RadioGroupItem value={v} className="sr-only" />{t}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label>Récupération</Label>
              <RadioGroup value={form.deliveryMethod} onValueChange={(v) => set("deliveryMethod", v)} className="flex gap-2 mt-1">
                {[["retrait", "Retrait"], ["livraison", "Livraison"]].map(([v, t]) => (
                  <label key={v} className={`flex-1 text-center text-sm rounded-lg border p-2 cursor-pointer ${form.deliveryMethod === v ? "border-[#581825] bg-[#F5EFEB]" : "border-[#E5DEC9]"}`}>
                    <RadioGroupItem value={v} className="sr-only" />{t}
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
          {form.deliveryMethod === "livraison" && (
            <div className="space-y-3 rounded-xl bg-[#F5EFEB] p-3">
              <div><Label>Adresse</Label><Input required value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code postal</Label><Input required value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} /></div>
                <div><Label>Ville</Label><Input required value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
              </div>
            </div>
          )}
          <div><Label>Date du premier retrait / envoi</Label><Input type="date" value={form.firstDate} onChange={(e) => set("firstDate", e.target.value)} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7]">
            {loading ? "Ajout..." : "Ajouter l'abonné"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/admin/subscribers", { params: { search, filter } });
    setList(data);
    setLoading(false);
  }, [search, filter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const exportCsv = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/admin/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "abonnes_boxvin.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export impossible. Vérifiez votre connexion."));
  };

  return (
    <div className="px-4 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-3xl font-bold text-[#581825]">Abonnés</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={exportCsv} data-testid="btn-export-csv" title="Export CSV">
            <Download className="w-4 h-4" />
          </Button>
          <Button size="icon" onClick={() => setAddOpen(true)} data-testid="btn-add-client"
            className="bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7]">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E675F]" />
        <Input data-testid="subscribers-search-input" placeholder="Rechercher par nom, ville..."
          value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-3">
        {FILTERS.map((f) => (
          <button key={f.v} data-testid={f.testId} onClick={() => setFilter(f.v)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${
              filter === f.v ? "bg-[#581825] text-[#FDFBF7] border-[#581825]" : "bg-white text-[#6E675F] border-[#E5DEC9]"
            }`}>
            {f.t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center pt-16"><Loader2 className="w-6 h-6 animate-spin text-[#581825]" /></div>
      ) : list.length === 0 ? (
        <p className="text-center text-[#6E675F] pt-16">Aucun abonné.</p>
      ) : (
        <div className="space-y-2.5">
          {list.map((s) => (
            <button key={s.id} data-testid="subscriber-row-item" onClick={() => navigate(`/admin/clients/${s.id}`)}
              className="w-full bg-white rounded-2xl border border-[#E5DEC9] p-4 text-left hover:border-[#C5A059] transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-[#110F0F]">{s.customer.firstName} {s.customer.lastName}</p>
                  <p className="text-sm text-[#6E675F]">{s.customer.phone}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge value={s.status} />
                  {s.orderPaymentStatus && <StatusBadge type="order_payment" value={s.orderPaymentStatus} />}
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-3 text-sm text-[#6E675F]">
                <span className="font-medium text-[#581825]">{s.planLabel}</span>
                <span>{s.bottlesPerMonth} bouteilles</span>
                <span className="font-semibold text-[#110F0F]">{s.monthlyPrice} €</span>
                <span className="flex items-center gap-1">
                  {s.deliveryMethod === "livraison" ? <Truck className="w-3.5 h-3.5" /> : <Store className="w-3.5 h-3.5" />}
                  {s.deliveryMethod === "livraison" ? "Livraison" : "Retrait"}
                </span>
                {s.nextDate && <span>· {new Date(s.nextDate + "T00:00:00").toLocaleDateString("fr-FR")}</span>}
                <ChevronRight className="w-4 h-4 ml-auto" />
              </div>
            </button>
          ))}
        </div>
      )}

      <AddClientDialog open={addOpen} onClose={() => setAddOpen(false)} onDone={load} />
    </div>
  );
}
